import { CampaignSubmissionTable } from '@/components/campaigns/CampaignSubmissionTable';
import { lazy, Suspense, useEffect, useMemo, useRef, useState, useCallback, type ReactNode } from 'react';
import { useParams, useNavigate, Link as RouterLink, useSearchParams, useLocation } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useQuery, useMutation, useLazyQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import {
  AlertTriangle, CheckCircle2, XCircle, ShieldAlert, Eye, DollarSign,
  Coins, Lightbulb, ExternalLink,
  Video, Scissors, Share2, Link2, ChevronRight, HelpCircle, LayoutTemplate, Lock,
  Download, FolderDown, Crown, Loader2, Import,
} from 'lucide-react';
import { parseRequiredHashtags } from '@/lib/parseRequiredHashtags';
import { getCpmCampaignStatus } from '@/lib/campaigns/getCpmCampaignStatus';
import { canImportCampaign } from '@/lib/campaigns/socialImport';
import { CampaignEndedNotice } from '@/components/campaigns/CampaignEndedNotice';
import { resolveVisibleMarkupPlatforms } from '@/lib/campaigns/requiredMarkup';
import { detectCampaignSubmissionPlatform, findCampaignSubmissionDuplicate } from '@/lib/campaigns/submissionPlatform';
import { submitCampaignClip } from '@/lib/campaigns/submitCampaignClip';
import { ACCOUNT_CHANGED_EVENT } from '@/lib/axios';
import { captionSubmissionFeedback } from '@/lib/campaigns/captionSubmissionFeedback';
import { getDuplicateSubmissionId } from '@/lib/campaigns/getDuplicateSubmissionId';
import { getAccountDisqualificationLabel } from '@/lib/campaigns/getAccountDisqualificationLabel';
import { CampaignAccountDisqualificationNotice } from '@/components/campaigns/CampaignAccountDisqualificationNotice';
import { filterVisibleSubmissions, countDisapprovedSubmissions } from '@/lib/campaigns/filterVisibleSubmissions';
import { getSubmissionFeedback } from '@/lib/campaigns/getSubmissionFeedback';
import { ReviewImagesStrip } from '@/components/campaigns/ReviewImagesStrip';
import { CaptionTokensNote } from '@/components/campaigns/CaptionTokensNote';
import { filterPublicReviewImages } from '@/lib/campaigns/reviewImages';
import { resolveAppealRowUi } from '@/lib/campaigns/resolveAppealRowUi';
import { mapOfficialMetricsError } from '@/lib/campaigns/mapOfficialMetricsError';
import { sortSubmissionsNewestFirst } from '@/lib/campaigns/sortSubmissionsNewestFirst';
import { getSubmissionVerdictKey, type SubmissionVerdictKey } from '@/lib/campaigns/getSubmissionVerdictKey';
import { shouldShowPendingBudgetExhaustedWarning } from '@/lib/campaigns/shouldShowPendingBudgetExhaustedWarning';
import { isCampaignBudgetExhausted } from '@/lib/campaigns/isCampaignBudgetExhausted';
import { resolveSubmissionEarningsDisplay } from '@/lib/campaigns/resolveSubmissionEarningsDisplay';
import { summarizeCampaignEarnings } from '@/lib/campaigns/summarizeCampaignEarnings';
import { summarizeLeaveCampaign } from '@/lib/campaigns/summarizeLeaveCampaign';
import { resolveSubmissionEarningsHint } from '@/lib/campaigns/resolveSubmissionEarningsHint';
import { isProExclusiveCampaign } from '@/lib/campaigns/isProExclusiveCampaign';
import { requiresOfficialConnection } from '@/lib/campaigns/officialConnectionPolicy';
import { promptsOfficialAccountLink } from '@/lib/campaigns/ownedCpmMediaResult';
import { describeMinViewsRequirement } from '@/lib/campaigns/describeMinViewsRequirement';
import { isSocialLinkingPlatform } from '@/lib/account-settings/visibleSocialLinkingPlatforms';
import { SiTiktok, SiInstagram, SiYoutube } from 'react-icons/si';
import Layout from '@/components/Layout';
import { useToast } from '@/hooks/use-toast';
import {
  MY_CPM_CAMPAIGN,
  MY_SOCIAL_CONNECTIONS,
  RESOLVE_MY_CPM_OWNED_MEDIA,
  CREATE_MY_CPM_CAMPAIGN_SUBMISSION,
  CREATE_MY_CPM_CAMPAIGN_CHANNEL_REQUEST,
  DELETE_MY_CPM_CAMPAIGN_CHANNEL_REQUEST,
  JOIN_CPM_CAMPAIGN,
  LEAVE_CPM_CAMPAIGN,
  CREATE_MY_CPM_CAMPAIGN_SUBMISSION_APPEAL,
  RESPOND_TO_MY_CPM_CAMPAIGN_SUBMISSION_APPEAL,
} from '@/queries/queries';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Progress } from '@/components/ui/progress';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Accordion, AccordionItem, AccordionTrigger, AccordionContent,
} from '@/components/ui/accordion';
import {
  TableCell,
  TableRow,
} from '@/components/ui/table';
import { formatCents, formatFilesize, formatDateTime } from '@/lib/format';
import CampaignBudgetBar from '@/components/CampaignBudgetBar';
import { CampaignLayoutModelsGuide } from '@/components/CampaignLayoutModelsGuide';
import CampaignVideoCard, {
  type CampaignVideoCardLibrary,
  type PreloadedCampaignLibrary,
} from '@/components/CampaignVideoCard';
import CopyLibraryDialog from '@/components/CopyLibraryDialog';
import { RemoveCampaignVideoDialog } from '@/components/campaigns/RemoveCampaignVideoDialog';
import CpmTermsModal from '@/components/CpmTermsModal';
import { CampaignWalletCard } from '@/components/CampaignWalletCard';
import { CampaignAiGuidelinesModal } from '@/components/CampaignAiGuidelinesModal';
import { AppealContestDialog } from '@/components/campaigns/AppealContestDialog';
import { useMyDataQuery } from '@/generated/graphql';
import { useIsPaidSubscriber } from '@/lib/quota/useIsPaidSubscriber';
import { isPaidSubscription } from '@/lib/quota/isPaidSubscription';
import { libraryHasActiveCpmCampaign } from '@/lib/quota/libraryHasActiveCpmCampaign';
import { CampaignBanner } from '@/components/campaigns/CampaignBanner';
import { CampaignAutomaticImportControl } from '@/components/campaigns/CampaignAutomaticImportControl';
import { CampaignClipSubmissionForm } from '@/components/campaigns/CampaignClipSubmissionForm';
import { CampaignRequiredMarkup } from '@/components/campaigns/CampaignRequiredMarkup';
const CampaignSocialImportDialog = lazy(() => import('@/components/campaigns/CampaignSocialImportDialog').then(module => ({ default: module.CampaignSocialImportDialog })));
const RankingCampaignPage = lazy(() => import('@/components/campaigns/RankingCampaignPage').then(module => ({ default: module.RankingCampaignPage })));
import { CampaignCategoryBadges } from '@/components/campaigns/CampaignCategoryBadges';
import { DEFAULT_CLIP_FORMAT } from '@/lib/projects/clipFormats';
import {
  getCampaignAiGuidelinesStorage,
  recordCampaignAiGuidelinesAcknowledgement,
  shouldShowCampaignAiGuidelines,
} from '@/lib/campaigns/campaignAiGuidelinesPreference';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';


function openOfficialAccountSettings(platform: string | null | undefined) {
  window.dispatchEvent(new CustomEvent('autoclipper:open-account-settings', {
    detail: isSocialLinkingPlatform(platform)
      ? { section: 'social', platform }
      : { section: 'social' },
  }));
}

function splitLines(text: string | null | undefined): string[] {
  if (!text) return [];
  return text
    .split(/\r?\n/)
    .map((l) => l.replace(/^[\s•\-*]+/, '').trim())
    .filter(Boolean);
}

/**
 * Parse a caption-examples text blob into platform-labeled entries.
 *
 * Recognized heading forms at the start of a line:
 *   INSTAGRAM:
 *   TIKTOK —
 *   YOUTUBE SHORTS -
 *
 * Headers must be uppercase (admin contract — see AdminCampaignEditPage help text);
 * a case-insensitive match would false-positive on user prose like "Olá: bem-vindo".
 *
 * A section with header matching DICA or NOTA is extracted as a footer tip instead
 * of a regular section. Single-dot lines (".") are stripped from section bodies so
 * admins can use them as visual padding without polluting output.
 */
function parseCaptionExamples(
  text: string | null | undefined,
): { sections: Array<{ header: string | null; body: string }>; tip: string | null } {
  if (!text) return { sections: [], tip: null };
  const lines = text.split(/\r?\n/);
  const allSections: Array<{ header: string | null; body: string }> = [];
  let currentHeader: string | null = null;
  let currentBody: string[] = [];
  const flush = () => {
    const cleanBody = currentBody
      .filter((l) => l !== '.')
      .join('\n')
      .trim();
    if (cleanBody) {
      allSections.push({ header: currentHeader, body: cleanBody });
    }
    currentBody = [];
  };
  for (const rawLine of lines) {
    const line = rawLine.trim();
    if (!line) continue;
    const match = line.match(/^([A-ZÇÃÁÉÍÓÚ][A-ZÇÃÁÉÍÓÚ\s]{2,30})\s*[:\-—–]\s*(.*)$/);
    if (match) {
      flush();
      currentHeader = match[1].trim();
      if (match[2]) currentBody.push(match[2].trim());
      continue;
    }
    currentBody.push(line);
  }
  flush();
  const sections = allSections.filter((s) => !s.header || !CAPTION_TIP_HEADERS.test(s.header));
  const tipSection = allSections.find((s) => s.header && CAPTION_TIP_HEADERS.test(s.header));
  return { sections, tip: tipSection?.body ?? null };
}

const PLATFORM_ORDER = ['tiktok', 'instagram', 'youtube'] as const;

// Left accent bar on the clip-link cell so the verdict color is visible on
// mobile without scrolling the table sideways to reach the Veredito column.
const VERDICT_ACCENT_BORDER: Record<SubmissionVerdictKey, string> = {
  disqualified: 'border-l-red-500',
  disapproved: 'border-l-red-500',
  approved: 'border-l-primary',
  pre_approved: 'border-l-sky-500',
  pending: 'border-l-amber-500',
};

const HOW_IT_WORKS_STEPS = [
  { icon: Video, key: 'chooseVideo' },
  { icon: Scissors, key: 'createClip' },
  { icon: Share2, key: 'postOnSocial' },
  { icon: Link2, key: 'sendLink' },
  { icon: Coins, key: 'earnByViews' },
] as const;

const RULES_DEFAULT_OPEN = ['allowed'];

const CAPTION_TIP_HEADERS = /^(DICA|NOTA|TIP|NOTE)\b/i;

const PARTICIPATING_INITIAL_SIZE = 8;

interface ParticipatingVideosSectionProps {
  libraries: any[];
  campaignChannelIds: string[];
  focusLibraryId?: string | null;
  onGenerateClips?: (library: CampaignVideoCardLibrary) => void;
  /** Platform admins only: remove a video from this campaign. */
  onRemoveVideo?: (library: CampaignVideoCardLibrary) => void;
}

function ParticipatingVideosSection({
  libraries,
  campaignChannelIds,
  focusLibraryId = null,
  onGenerateClips,
  onRemoveVideo,
}: ParticipatingVideosSectionProps) {
  const { t } = useTranslation();
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    if (!focusLibraryId) return;
    const index = libraries.findIndex((library) => library.id === focusLibraryId);
    if (index >= PARTICIPATING_INITIAL_SIZE) {
      setShowAll(true);
    }
  }, [focusLibraryId, libraries]);

  if (libraries.length === 0) {
    return (
      <section className="space-y-3">
        <h2 className="text-xl font-semibold">
          {t('campaign.participatingVideos.title')}
        </h2>
        <p className="text-sm text-muted-foreground">
          {t('campaign.participatingVideos.empty')}
        </p>
      </section>
    );
  }

  const visible = showAll ? libraries : libraries.slice(0, PARTICIPATING_INITIAL_SIZE);
  const hasMore = libraries.length > PARTICIPATING_INITIAL_SIZE;

  return (
    <section className="space-y-4">
      <div className="flex items-center gap-3">
        <h2 className="text-xl font-semibold">{t('campaign.participatingVideos.title')}</h2>
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary border border-primary/30">
          {t('campaign.participatingVideos.available', { count: libraries.length })}
        </span>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
        {visible.map((library) => (
          <CampaignVideoCard
            key={library.id}
            library={library}
            campaignChannelIds={campaignChannelIds}
            onGenerateClips={onGenerateClips}
            quotaExempt={libraryHasActiveCpmCampaign(library)}
            onRemoveFromCampaign={onRemoveVideo}
          />
        ))}
      </div>
      {hasMore && (
        <div className="flex justify-center pt-2">
          <Button
            variant="outline"
            size={showAll ? 'sm' : 'default'}
            className={showAll ? undefined : 'gap-2 border-primary/30 hover:bg-primary/10 hover:text-primary transition-colors'}
            onClick={() => setShowAll((v) => !v)}
          >
            {showAll
              ? t('campaign.participatingVideos.showLess')
              : t('campaign.participatingVideos.showAll', { count: libraries.length })}
          </Button>
        </div>
      )}
    </section>
  );
}

export default function CampaignDetail() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialLibraryIdRef = useRef(searchParams.get('libraryId'));
  const libraryDeepLinkApplied = useRef(false);
  const preloadedLibrary = (location.state as { preloadedLibrary?: PreloadedCampaignLibrary } | null)?.preloadedLibrary ?? null;
  const [copyDialogLibrary, setCopyDialogLibrary] = useState<PreloadedCampaignLibrary | null>(preloadedLibrary);
  const [focusLibraryId, setFocusLibraryId] = useState<string | null>(initialLibraryIdRef.current);
  const { data, loading, error, refetch } = useQuery(MY_CPM_CAMPAIGN, {
    variables: { id: id || '' },
    skip: !id,
    pollInterval: 60_000,
  });
  const {
    data: socialConnectionsData,
    refetch: refetchSocialConnections,
  } = useQuery(MY_SOCIAL_CONNECTIONS, {
    fetchPolicy: 'network-only',
  });
  useEffect(() => {
    const handleSocialConnection = (event: MessageEvent) => {
      if (event.data?.type === 'autoclipper:social-connection' && event.data.success) {
        void refetchSocialConnections();
      }
    };
    window.addEventListener('message', handleSocialConnection);
    return () => window.removeEventListener('message', handleSocialConnection);
  }, [refetchSocialConnections]);
  const [submit, { loading: submitting }] = useMutation(CREATE_MY_CPM_CAMPAIGN_SUBMISSION);
  const [resolveOwnedMedia] = useLazyQuery(RESOLVE_MY_CPM_OWNED_MEDIA, {
    fetchPolicy: 'network-only',
    errorPolicy: 'all',
  });
  const [resolvingOwnership, setResolvingOwnership] = useState(false);
  const [createChannelRequest, { loading: addingChannel }] = useMutation(
    CREATE_MY_CPM_CAMPAIGN_CHANNEL_REQUEST,
  );
  const [deleteChannelRequest] = useMutation(DELETE_MY_CPM_CAMPAIGN_CHANNEL_REQUEST);
  const [joinCampaign, { loading: joining }] = useMutation(JOIN_CPM_CAMPAIGN);
  const [leaveCampaign, { loading: leaving }] = useMutation(LEAVE_CPM_CAMPAIGN);
  const [leaveConfirmOpen, setLeaveConfirmOpen] = useState(false);
  const [createAppeal, { loading: creatingAppeal }] = useMutation(CREATE_MY_CPM_CAMPAIGN_SUBMISSION_APPEAL);
  const [respondToAppeal, { loading: respondingToAppeal }] = useMutation(
    RESPOND_TO_MY_CPM_CAMPAIGN_SUBMISSION_APPEAL,
  );
  const appealing = creatingAppeal || respondingToAppeal;
  const [appealTarget, setAppealTarget] = useState<{
    id: string;
    originalReason: string;
    mode: 'contest' | 'followUp';
    appealId?: string;
    auditorReply?: string;
  } | null>(null);
  const [socialImportOpen, setSocialImportOpen] = useState(false);
  const submissionMounted = useRef(true);
  const submissionGeneration = useRef(0);
  const submissionCampaignId = useRef(id); submissionCampaignId.current = id;
  useEffect(() => {
    submissionMounted.current = true;
    const invalidate = () => { submissionGeneration.current++; };
    const accountChanged = () => { submissionGeneration.current++; setSocialImportOpen(false); setResolvingOwnership(false); };
    window.addEventListener(ACCOUNT_CHANGED_EVENT, accountChanged);
    return () => { submissionMounted.current = false; invalidate(); window.removeEventListener(ACCOUNT_CHANGED_EVENT, accountChanged); };
  }, []);
  const [url, setUrl] = useState('');
  const [channelUrl, setChannelUrl] = useState('');
  const { toast } = useToast();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [errorConnectPlatform, setErrorConnectPlatform] = useState<string | null>(null);
  const [errorSubmissionId, setErrorSubmissionId] = useState<string | null>(null);
  const [submissionToReveal, setSubmissionToReveal] = useState<{ id: string } | null>(null);
  const revealAfterDialogClose = useRef(false);
  const showCampaignError = (
    message: string,
    code: string | null = null,
    platform: string | null = null,
    existingSubmissionId: string | null = null,
  ) => {
    setErrorMessage(message);
    setErrorSubmissionId(existingSubmissionId);
    setErrorConnectPlatform(
      promptsOfficialAccountLink(code) && isSocialLinkingPlatform(platform)
        ? platform
        : null,
    );
  };
  const clearCampaignError = () => {
    setErrorMessage(null);
    setErrorConnectPlatform(null);
    setErrorSubmissionId(null);
  };
  const [termsOpen, setTermsOpen] = useState(false);
  const [aiGuidelinesOpen, setAiGuidelinesOpen] = useState(false);
  const [justSubmitted, setJustSubmitted] = useState(false);
  // When a join is intercepted by the terms modal, remember it so we can
  // resume the join automatically once the terms are accepted — otherwise the
  // user accepts terms but stays un-joined and the submission form never
  // appears (they'd have to click "Join" a second time).
  const pendingJoinRef = useRef(false);

  const detectedChannelPlatform = (() => {
    const trimmed = channelUrl.trim();
    if (/^https?:\/\/(www\.)?youtube\.com\/@/.test(trimmed)) return 'youtube';
    if (/^https?:\/\/(www\.)?youtube\.com\/channel\//.test(trimmed)) return 'youtube';
    if (/^https?:\/\/(www\.)?instagram\.com\/[^/]+\/?$/.test(trimmed)) return 'instagram';
    if (/^https?:\/\/(www\.)?tiktok\.com\/@[^/]+\/?$/.test(trimmed)) return 'tiktok';
    return '';
  })();
  const { data: myDataResponse, refetch: refetchMyData } = useMyDataQuery();
  // Widened from Pro+-only to any paid plan on 03/08/2026 (CEO request) — see
  // deriveCampaignAccess.ts docstring for the backend caveat.
  const isPaidUser = useIsPaidSubscriber();
  const isPaidPlan = useMemo(
    () => isPaidSubscription(myDataResponse?.myData?.subscription),
    [myDataResponse?.myData?.subscription],
  );
  const myData = myDataResponse?.myData;
  const hasLoadedMyData = !!myData;
  const cpmTermsCurrentVersion = myData?.cpm_terms_current_version || '1.0';
  const hasAcceptedCpmTerms = !!myData
    && !!myData.cpm_terms_accepted_version
    && myData.cpm_terms_accepted_version === cpmTermsCurrentVersion;

  const campaign: any = data?.myCpmCampaign;
  const campaignStatus = getCpmCampaignStatus(campaign, new Date());
  const isActive = campaignStatus === 'active';
  const isEnded = campaignStatus === 'ended';

  const participatingLibraries = useMemo(
    () => {
      if (!campaign) return [];
      return (campaign.participating_libraries || []).map((lib: any) => ({
        ...lib,
        active_cpm_campaigns: [
          ...(isActive ? [{ id: campaign.id }] : []),
          ...(lib.active_cpm_campaigns || []).filter((active: { id: string }) => active.id !== campaign.id),
        ],
      }));
    },
    [campaign, isActive],
  );

  const currentCopyLibrary = participatingLibraries.find((library) => library.id === copyDialogLibrary?.id)
    ?? copyDialogLibrary;
  const cpmUnlocked = libraryHasActiveCpmCampaign({
    active_cpm_campaigns: currentCopyLibrary?.active_cpm_campaigns?.filter(
      (active: { id: string }) => isActive || active.id !== campaign?.id,
    ),
  });

  const openCopyDialog = useCallback((library: PreloadedCampaignLibrary) => {
    setCopyDialogLibrary(library);
  }, []);

  // A shared channel can bring another campaign's videos in (Economizare's gas
  // videos on its water campaign); platform admins remove them from the card.
  const [videoToRemove, setVideoToRemove] = useState<CampaignVideoCardLibrary | null>(null);
  const requestVideoRemoval = useCallback((library: CampaignVideoCardLibrary) => {
    setVideoToRemove(library);
  }, []);
  const onRemoveVideo = myData?.is_admin ? requestVideoRemoval : undefined;

  useEffect(() => {
    if (libraryDeepLinkApplied.current || !campaign) return;
    const target = initialLibraryIdRef.current;
    if (!target) {
      libraryDeepLinkApplied.current = true;
      return;
    }
    if (preloadedLibrary?.id === target) {
      libraryDeepLinkApplied.current = true;
      if (searchParams.has('libraryId')) {
        setSearchParams((prev) => {
          prev.delete('libraryId');
          return prev;
        }, { replace: true });
      }
      return;
    }

    const match = participatingLibraries.find((library) => library.id === target);
    if (match) {
      setCopyDialogLibrary(match);
      setFocusLibraryId(target);

      window.setTimeout(() => {
        const node = document.querySelector(
          `[data-library-id="${CSS.escape(target)}"]`,
        ) as HTMLElement | null;
        if (node) {
          node.scrollIntoView({ behavior: 'smooth', block: 'center' });
          const ringClasses = ['ring-2', 'ring-primary', 'ring-offset-2', 'ring-offset-background'];
          node.classList.add(...ringClasses);
          window.setTimeout(() => node.classList.remove(...ringClasses), 2500);
        }
      }, 200);
    }

    libraryDeepLinkApplied.current = true;
    if (searchParams.has('libraryId')) {
      setSearchParams((prev) => {
        prev.delete('libraryId');
        return prev;
      }, { replace: true });
    }
  }, [campaign, participatingLibraries, preloadedLibrary, searchParams, setSearchParams]);

  useEffect(() => {
    if (!campaign?.id || !hasLoadedMyData) return;
    if (isPaidUser || !isActive) {
      setAiGuidelinesOpen(false);
      return;
    }
    setAiGuidelinesOpen(
      shouldShowCampaignAiGuidelines(
        getCampaignAiGuidelinesStorage(window),
        campaign.id,
      ),
    );
  }, [campaign?.id, hasLoadedMyData, isPaidUser, isActive]);

  const { sections: captionExamples, tip: captionTip } = useMemo(
    () => parseCaptionExamples(campaign?.caption_examples),
    [campaign?.caption_examples],
  );
  const rulesAllowed = useMemo(
    () => splitLines(campaign?.rules_allowed),
    [campaign?.rules_allowed],
  );
  const rulesForbidden = useMemo(
    () => splitLines(campaign?.rules_forbidden),
    [campaign?.rules_forbidden],
  );
  const submissions = useMemo<any[]>(
    () => campaign?.my_submissions || [],
    [campaign?.my_submissions],
  );
  const disapprovedCount = useMemo(() => countDisapprovedSubmissions(submissions), [submissions]);
  const leaveImpact = useMemo(() => summarizeLeaveCampaign(submissions), [submissions]);
  const [hideDisapproved, setHideDisapproved] = useState(false);
  const visibleSubmissions = useMemo(
    () => filterVisibleSubmissions(submissions, { hideDisapproved }),
    [submissions, hideDisapproved],
  );
  const earningsSummary = useMemo(
    () => summarizeCampaignEarnings(submissions, {
      cpm_rate_cents: campaign?.cpm_rate_cents || 0,
      min_views_threshold: campaign?.min_views_threshold,
      max_payout_per_clip_cents: campaign?.max_payout_per_clip_cents,
    }),
    [
      submissions,
      campaign?.cpm_rate_cents,
      campaign?.min_views_threshold,
      campaign?.max_payout_per_clip_cents,
    ],
  );
  const errorDuplicateSubmission = submissions.find((submission) => submission.id === errorSubmissionId);
  const revealSubmission = (submissionId: string) => {
    if (!submissions.some((submission) => submission.id === submissionId)) return;
    revealAfterDialogClose.current = !!errorMessage;
    setHideDisapproved(false);
    setSubmissionToReveal({ id: submissionId });
    clearCampaignError();
  };
  useEffect(() => {
    if (!submissionToReveal) return;
    const frame = requestAnimationFrame(() => {
      const row = document.getElementById(`campaign-submission-${submissionToReveal.id}`);
      row?.scrollIntoView({
        behavior: window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
        block: 'center',
        inline: 'nearest',
      });
      row?.focus({ preventScroll: true });
    });
    return () => cancelAnimationFrame(frame);
  }, [submissionToReveal]);
  useEffect(() => {
    setSubmissionToReveal(null);
    setErrorSubmissionId(null);
  }, [id]);
  const { submissionsByPlatform, platformGroups } = useMemo(() => {
    const sorted = sortSubmissionsNewestFirst(visibleSubmissions);
    const grouped: Record<string, any[]> = {};
    for (const s of sorted) {
      const p = (s.platform || 'other').toLowerCase();
      (grouped[p] ||= []).push(s);
    }
    const known = PLATFORM_ORDER.filter((p) => grouped[p]);
    const extras = Object.keys(grouped).filter(
      (p) => !PLATFORM_ORDER.includes(p as typeof PLATFORM_ORDER[number]),
    );
    return { submissionsByPlatform: grouped, platformGroups: [...known, ...extras] };
  }, [visibleSubmissions]);

  if (loading) {
    return (
      <Layout>
        <main className="flex-1 p-4 sm:p-6 lg:p-8 overflow-y-auto">
          <p className="text-muted-foreground">{t('campaign.loading')}</p>
        </main>
      </Layout>
    );
  }
  if (error) {
    return (
      <Layout>
        <main className="flex-1 p-4 sm:p-6 lg:p-8 overflow-y-auto">
          <p className="text-destructive">{error.message}</p>
        </main>
      </Layout>
    );
  }
   
  if (!campaign) {
    return (
      <Layout>
        <main className="flex-1 p-4 sm:p-6 lg:p-8 overflow-y-auto">
          <p className="text-muted-foreground">{t('campaign.notFound')}</p>
        </main>
      </Layout>
    );
  }

  const spent = (campaign.budget_total_cents || 0) - (campaign.budget_remaining_cents || 0);
  const budgetExhausted = isCampaignBudgetExhausted({
    remainingCents: campaign.budget_remaining_cents,
    totalCents: campaign.budget_total_cents,
  });
  const budgetLow = !budgetExhausted
    && campaign.budget_total_cents > 0
    && (campaign.budget_remaining_cents || 0) / campaign.budget_total_cents < 0.25;

  const requiresApproval = !!campaign?.requires_channel_approval;
  const approvedPlatforms = new Set<string>(campaign?.my_approved_platforms || []);
  // Invite-only campaigns are shown but marked exclusive and locked for users
  // who haven't been invited (no approved channel request) and don't have
  // any paid plan (Starter/Plus/Pro+) — either bypasses the lock.
  // "Selecionados" is a fully exclusive mode, not a third way past the invite
  // lock: neither a paid plan nor an approved channel opens it, so it gets its
  // own lock rather than folding into isInviteOnlyLocked.
  const isSelectedOnly = !!campaign?.selected_only;
  const isSelectedLocked = campaign.campaign_type !== 'ranking' && isSelectedOnly && !campaign?.is_selected_user;
  const isInviteOnlyLocked = isSelectedLocked
    || (campaign.campaign_type !== 'ranking' && !!campaign?.invite_only && approvedPlatforms.size === 0 && !isPaidUser);
  // Invite-only campaign unlocked for the viewer because they have a paid
  // plan — mark it as a paid-plan perk in the header.
  const isProExclusive = isProExclusiveCampaign({
    inviteOnly: !!campaign?.invite_only,
    isPaidUser,
  });

  const myChannelRequests: any[] = campaign?.my_channel_requests || [];
  const myYoutubeApproved = myChannelRequests.filter(
     
    (r: any) => r.platform === 'youtube' && r.status === 'approved',
  );

  const detectedPlatform = detectCampaignSubmissionPlatform(url, campaign.campaign_type === 'ranking');
  const requiresOfficialSocialConnection = requiresOfficialConnection(detectedPlatform);
  const matchingSocialConnections = (
    socialConnectionsData?.mySocialConnections || []
  ).filter(
    (connection: any) => requiresOfficialSocialConnection
      && connection.platform === detectedPlatform
      && connection.status === 'active',
  );
  const hasMatchingSocialConnection = matchingSocialConnections.length > 0;

  let submitBlockedReason: string | null = null;
  if (isSelectedLocked) {
    submitBlockedReason = t('earnPage.selectedOnly.popoverText');
  } else if (isInviteOnlyLocked) {
    submitBlockedReason = t('earnPage.inviteOnly.popoverText');
  } else if (
    requiresOfficialSocialConnection
    && !hasMatchingSocialConnection
  ) {
    submitBlockedReason = t('campaign.submit.connectionRequired', {
      platform: t(`campaign.channels.platformLabel.${detectedPlatform}`),
    });
  } else if (requiresApproval && detectedPlatform && detectedPlatform !== 'facebook') {
    if (!approvedPlatforms.has(detectedPlatform)) {
      submitBlockedReason = t('campaign.submit.channelRequired', {
        platform: t(`campaign.channels.platformLabel.${detectedPlatform}`),
      });
    } else if (detectedPlatform === 'youtube') {
      const m = url.match(/youtube\.com\/(@[^/?]+)/i);
      const handleInUrl = m?.[1]?.toLowerCase() || null;
      if (
        handleInUrl
         
        && !myYoutubeApproved.some((r: any) => (r.handle || '').toLowerCase() === handleInUrl)
      ) {
        submitBlockedReason = t('campaign.submit.channelMismatch');
      }
    }
  }
  const duplicateOf = findCampaignSubmissionDuplicate(url, submissions, campaign.campaign_type === 'ranking');
  const duplicateBlocksSubmission = !submitBlockedReason && !!url.trim() && !!duplicateOf;
  if (duplicateBlocksSubmission) {
    submitBlockedReason = t('campaign.submit.duplicateError');
  }
  const canSubmit = !!url.trim()
    && !!detectedPlatform
    && !submitting
    && !resolvingOwnership
    && !submitBlockedReason;

  const isJoined = !!campaign?.is_joined;
  const isFreeLimitBlocked = campaign?.join_blocked_reason === 'free_campaign_limit';

  const performJoin = async () => {
    if (!id) return;
    try {
      await joinCampaign({ variables: { campaignId: id } });
      // The global Apollo watchQuery policy is `no-cache`, so the active
      // MyCpmCampaign query does NOT pick up mutation `refetchQueries` cache
      // writes — we must refetch the observer directly (same pattern as
      // onSubmit) for `is_joined` to flip and the submission form to appear.
      await refetch();
      toast({ title: t('campaign.join.successTitle') });
    } catch (err: any) {
      const raw = err?.message || '';
      if (raw.includes('cpm_terms_not_accepted')) { setTermsOpen(true); return; }
      if (raw.includes('free_campaign_limit')) {
        showCampaignError(t('campaign.join.errors.free_campaign_limit'));
        return;
      }
      if (raw.includes('invite_required')) {
        showCampaignError(t('campaign.join.errors.invite_required'));
        return;
      }
      showCampaignError(raw);
    }
  };

  const onJoin = async () => {
    if (!id) return;
    if (!hasAcceptedCpmTerms) {
      pendingJoinRef.current = true;
      setTermsOpen(true);
      return;
    }
    await performJoin();
  };

  const acknowledgeAiGuidelines = (dismissAll: boolean) => {
    recordCampaignAiGuidelinesAcknowledgement(
      getCampaignAiGuidelinesStorage(window),
      campaign.id,
      dismissAll,
    );
    setAiGuidelinesOpen(false);
  };

  const handleAiGuidelinesAcknowledge = async (dismissAll: boolean) => {
    acknowledgeAiGuidelines(dismissAll);
    if (!isJoined) await onJoin();
  };

  const onLeave = async () => {
    if (!id) return;
    const { approvedCount } = leaveImpact;
    try {
      await leaveCampaign({ variables: { campaignId: id } });
      setLeaveConfirmOpen(false);
      await refetch();
      toast({
        title: t('campaign.leave.successTitle'),
        description: approvedCount > 0 ? t('campaign.leave.successDescription') : undefined,
      });
    } catch {
      setLeaveConfirmOpen(false);
      showCampaignError(t('campaign.leave.errors.generic'));
    }
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (campaign.campaign_type !== 'ranking' && getCpmCampaignStatus(campaign, new Date()) !== 'active') return;
    if (!detectedPlatform) return;
    if (duplicateOf) {
      showCampaignError(t('campaign.submit.duplicateError'), 'duplicate_submission', detectedPlatform, duplicateOf.id);
      return;
    }
    if (!hasAcceptedCpmTerms) {
      setTermsOpen(true);
      return;
    }
    const version = submissionGeneration.current;
    const token = localStorage.getItem('token');
    const currentSubmission = () => submissionMounted.current && submissionGeneration.current === version
      && submissionCampaignId.current === campaign.id && localStorage.getItem('token') === token;
    try {
      await submitCampaignClip({ campaignId: campaign.id, platform: detectedPlatform, url,
        isCurrent: currentSubmission,
        onResolvingOwnership: setResolvingOwnership,
        resolveOwnership: variables => resolveOwnedMedia({ variables }),
        createSubmission: input => submit({ variables: { input } }),
      });
      if (!currentSubmission()) return;
      setUrl('');
      setJustSubmitted(true);
      toast({
        title: t('campaign.submit.successTitle'),
        description: t('campaign.submit.successDescription'),
      });
      await refetch();
     
    } catch (err: any) {
      if (!currentSubmission() || err?.message === 'import_cancelled') return;
      const captionFeedback = captionSubmissionFeedback(err, t);
      if (captionFeedback) {
        showCampaignError(captionFeedback, 'cpm_required_caption_tokens_missing', detectedPlatform);
        return;
      }
      const raw = err?.message || 'Submission failed';
      if (raw.includes('cpm_terms_not_accepted')) {
        setTermsOpen(true);
        return;
      }
      const code = raw.replace(/^.*?:\s*/, '').trim();
      const existingSubmissionId = code === 'duplicate_submission' ? getDuplicateSubmissionId(err) : null;
      if (existingSubmissionId && !submissions.some((submission) => submission.id === existingSubmissionId)) {
        try {
          await refetch();
        } catch {
          // Preserve the duplicate message if the current list cannot be refreshed.
        }
      }
      const i18nKey = `campaign.submit.errors.${code}`;
      const platformLabel = t(`campaign.channels.platformLabel.${detectedPlatform}`);
      const translated = t(i18nKey, { defaultValue: '', platform: platformLabel, account: code === 'cpm_account_disqualified' ? getAccountDisqualificationLabel(err) : '' });
      showCampaignError(
        translated || t('campaign.submit.errors.generic', { defaultValue: raw }),
        code,
        detectedPlatform,
        existingSubmissionId,
      );
    }
  };

  const onCopyHashtags = async (text: string, toastTitle?: string) => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      toast({ title: toastTitle ?? t('campaign.hashtagsCopied') });
    } catch {
      showCampaignError('Clipboard unavailable');
    }
  };

  const handleAddChannel = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!detectedChannelPlatform || !id) return;
    try {
      await createChannelRequest({
        variables: { campaignId: id, platform: detectedChannelPlatform, url: channelUrl.trim() },
      });
      await refetch();
      setChannelUrl('');
     
    } catch (err: any) {
      const code = (err?.message || '').replace(/^.*?:\s*/, '').trim();
      const lookupKey =
        code === 'duplicate_channel_request' ? 'campaign.channels.duplicate'
          : code === 'invalid_channel_url' ? 'campaign.channels.invalidUrl'
            : code === 'cannot_delete_reviewed_request' ? 'campaign.channels.cannotDelete'
              : null;
      showCampaignError(lookupKey ? t(lookupKey) : code);
    }
  };

  const handleCancelRequest = async (requestId: string) => {
    if (!id) return;
    try {
      await deleteChannelRequest({ variables: { id: requestId } });
      await refetch();

    } catch (err: any) {
      const code = (err?.message || '').replace(/^.*?:\s*/, '').trim();
      showCampaignError(
        code === 'cannot_delete_reviewed_request'
          ? t('campaign.channels.cannotDelete')
          : code,
      );
    }
  };

  const handleAppealSubmit = async ({
    argument,
    extraUrl,
    evidenceImageUrls,
  }: {
    argument: string;
    extraUrl: string;
    evidenceImageUrls: string[];
  }) => {
    if (!appealTarget) return;
    try {
      if (appealTarget.mode === 'followUp') {
        if (!appealTarget.appealId) return;
        await respondToAppeal({
          variables: {
            id: appealTarget.appealId,
            argument,
            extraUrl: extraUrl || undefined,
            evidenceImageUrls: evidenceImageUrls.length > 0 ? evidenceImageUrls : undefined,
          },
        });
      } else {
        await createAppeal({
          variables: {
            submissionId: appealTarget.id,
            argument,
            extraUrl: extraUrl || undefined,
            evidenceImageUrls: evidenceImageUrls.length > 0 ? evidenceImageUrls : undefined,
          },
        });
      }
      toast({
        title: t('campaign.submissions.appeal.pendingBadge'),
        description: t('campaign.submissions.appeal.pendingCopy'),
      });
      setAppealTarget(null);
      await refetch();
    } catch (err: any) {
      const code = (err?.message || '').replace(/^.*?:\s*/, '').trim();
      const i18nKey = `campaign.submissions.appeal.errors.${code}`;
      const translated = t(i18nKey, { defaultValue: '' });
      toast({
        title: translated || t('campaign.submissions.appeal.errors.generic'),
        variant: 'destructive',
      });
    }
  };

  const parsedHashtags = parseRequiredHashtags(campaign.required_hashtags);
  const visibleMarkupPlatforms = resolveVisibleMarkupPlatforms(campaign.allowed_platforms);
  const activePlatforms = [
    parsedHashtags.tiktok && visibleMarkupPlatforms.includes('tiktok') ? 'tiktok' : null,
    parsedHashtags.instagram && visibleMarkupPlatforms.includes('instagram') ? 'instagram' : null,
    parsedHashtags.youtube && visibleMarkupPlatforms.includes('youtube') ? 'youtube' : null,
  ].filter(Boolean) as string[];
  const maxPayoutPerClipCents = campaign.max_payout_per_clip_cents || 0;
  const minViewsThreshold = campaign.min_views_threshold || 0;
  const minViews = describeMinViewsRequirement(campaign.min_views_threshold);
  // Budget / rate / min views / remaining always render (min views says "no
  // minimum" when there is none); only the per-clip cap is optional, so the
  // grid fits 4 or 5 tiles without squeezing any into an unreadable column.
  const metricGridColumnsClass = maxPayoutPerClipCents > 0
    ? 'sm:grid-cols-2 lg:grid-cols-5'
    : 'sm:grid-cols-2 lg:grid-cols-4';
  const hasAttention = !!campaign.rules_attention?.trim();
  const hasWarning = !!campaign.rules_warning?.trim();
  const hasPunishments = hasAttention || hasWarning;
  // Only campaigns that enforce a post layout get the layout-models guide.
  const hasLayoutModels = !!campaign?.enforced_post_layout;

  const channelApprovalSection = requiresApproval && isJoined ? (
<section id="campaign-channels" className="mt-8">
              <h2 className="text-xl font-semibold mb-4">{t('campaign.channels.title')}</h2>

              {myChannelRequests.length === 0 && (
                <p className="text-muted-foreground mb-4">{t('campaign.channels.empty')}</p>
              )}

              {(['pending', 'approved', 'rejected'] as const).map((group) => {

                const rows = myChannelRequests.filter((r: any) => r.status === group);
                if (rows.length === 0) return null;
                const labelKey = `campaign.channels.status${group[0].toUpperCase()}${group.slice(1)}`;
                return (
                  <div key={group} className="mb-4">
                    <h3 className="font-medium mb-2">{t(labelKey)}</h3>
                    <ul className="space-y-2">
                      { }
                      {rows.map((r: any) => (
                        <li
                          key={r.id}
                          className="flex items-center justify-between p-3 border rounded"
                        >
                          <div className="flex items-center gap-3 min-w-0">
                            <span className="text-xs uppercase text-muted-foreground">
                              {t(`campaign.channels.platformLabel.${r.platform}`)}
                            </span>
                            <a
                              href={r.submitted_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="font-medium truncate"
                            >
                              {r.handle || r.submitted_url}
                            </a>
                            {r.review_reason && (r.status === 'rejected' || r.status === 'approved') && (
                              <span
                                className={
                                  r.status === 'approved'
                                    ? 'text-sm text-muted-foreground'
                                    : 'text-sm text-red-600'
                                }
                              >
                                — {r.review_reason}
                              </span>
                            )}
                          </div>
                          {r.status === 'pending' && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleCancelRequest(r.id)}
                            >
                              {t('campaign.channels.cancel')}
                            </Button>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}

              <form onSubmit={handleAddChannel} className="flex gap-2 items-center mt-4">
                <Input
                  placeholder="https://youtube.com/@yourchannel"
                  value={channelUrl}
                  onChange={(e) => setChannelUrl(e.target.value)}
                  className="flex-1"
                />
                <Button type="submit" disabled={!detectedChannelPlatform || addingChannel}>
                  {t('campaign.channels.add')}
                </Button>
              </form>
              {channelUrl.trim() && !detectedChannelPlatform && (
                <p className="text-sm text-amber-600 mt-1">
                  {t('campaign.channels.invalidUrl')}
                </p>
              )}
            </section>
  ) : null;

  const requiredMarkup = <CampaignRequiredMarkup hashtags={campaign.required_hashtags} mention={campaign.required_mention} allowedPlatforms={campaign.allowed_platforms} ranking={campaign.campaign_type === 'ranking'} onCopy={onCopyHashtags} />;

  const canShowSocialImport = canImportCampaign(campaign, true) && !isInviteOnlyLocked;
  const clipSubmissionForm = <>{isJoined ? <CampaignAccountDisqualificationNotice campaignId={campaign.id} /> : null}<CampaignClipSubmissionForm
    automaticImportControl={canShowSocialImport ? <CampaignAutomaticImportControl key={campaign.id} campaign={campaign} acceptedTerms={hasAcceptedCpmTerms} disabled={submitting || resolvingOwnership || socialImportOpen} /> : null}
    importAction={canShowSocialImport ? <Button type="button" variant="outline" disabled={!hasAcceptedCpmTerms || submitting || resolvingOwnership} onClick={() => setSocialImportOpen(true)}><Import className="mr-2 h-4 w-4" />{t('campaign.socialImport.button')}</Button> : null}
    ranking={campaign.campaign_type === 'ranking'}
    onSubmit={onSubmit} url={url}
    onUrlChange={value => { setUrl(value); setJustSubmitted(false); setSubmissionToReveal(null); }}
    platform={detectedPlatform} blockedReason={submitBlockedReason} canSubmit={canSubmit}
    acceptedTerms={hasAcceptedCpmTerms} onAcceptTerms={() => setTermsOpen(true)} submitted={justSubmitted}
    blockedAction={duplicateBlocksSubmission && duplicateOf
      ? <button type="button" className="underline" onClick={() => revealSubmission(duplicateOf.id)}>{t('campaign.submit.viewSubmission')}</button>
      : requiresOfficialSocialConnection && !hasMatchingSocialConnection
        ? <button type="button" className="underline" onClick={() => openOfficialAccountSettings(detectedPlatform)}>{t('account.connectSocialMedia')}</button>
        : <a href="#campaign-channels" className="underline">{t('campaign.channels.title')}</a>}
  />
  {canShowSocialImport && socialImportOpen && <Suspense fallback={<p role="status">{t('campaign.socialImport.loading')}</p>}>
    <CampaignSocialImportDialog key={campaign.id} campaign={campaign} acceptedTerms={hasAcceptedCpmTerms} submissions={submissions}
      onImported={refetch} onClose={() => setSocialImportOpen(false)} />
  </Suspense>}</>;

  const submissionErrorDialog = <Dialog open={!!errorMessage} onOpenChange={(open) => { if (!open) clearCampaignError(); }}>
            <DialogContent onCloseAutoFocus={(event) => {
              if (!revealAfterDialogClose.current) return;
              event.preventDefault();
              revealAfterDialogClose.current = false;
              document.getElementById(`campaign-submission-${submissionToReveal?.id}`)?.focus({ preventScroll: true });
            }}>
              <DialogHeader>
                <DialogTitle>{t('campaign.error')}</DialogTitle>
                <DialogDescription>{errorMessage}</DialogDescription>
              </DialogHeader>
              {errorDuplicateSubmission && (
                <DialogFooter>
                  <Button type="button" onClick={() => revealSubmission(errorDuplicateSubmission.id)}>
                    <Eye className="mr-2 h-4 w-4" />
                    {t('campaign.submit.viewSubmission')}
                  </Button>
                </DialogFooter>
              )}
              {errorConnectPlatform && (
                <DialogFooter>
                  <Button
                    type="button"
                    onClick={() => {
                      openOfficialAccountSettings(errorConnectPlatform);
                      clearCampaignError();
                    }}
                  >
                    {t('account.connectSocialMedia')}
                  </Button>
                </DialogFooter>
              )}
            </DialogContent>
          </Dialog>;

  if (campaign.campaign_type === 'ranking') {
    return <Suspense fallback={<p aria-busy="true">{t('campaign.loading')}</p>}><RankingCampaignPage campaign={campaign} onJoin={onJoin} joining={joining} channelApproval={channelApprovalSection} submission={clipSubmissionForm} markup={requiredMarkup} termsAccepted={hasAcceptedCpmTerms}>
      <ParticipatingVideosSection libraries={participatingLibraries} campaignChannelIds={(campaign.youtube_channels || []).map((channel: { channel_id: string }) => channel.channel_id)} onGenerateClips={openCopyDialog} onRemoveVideo={onRemoveVideo} />
      <RemoveCampaignVideoDialog campaignId={campaign.id} video={videoToRemove} onOpenChange={(open) => { if (!open) setVideoToRemove(null); }} onRemoved={() => { void refetch(); }} />
      <CopyLibraryDialog isOpen={!!copyDialogLibrary} onClose={() => setCopyDialogLibrary(null)} title={t('library.generateAIClips')} isPaidPlan={isPaidPlan} cpmUnlocked={cpmUnlocked} library={copyDialogLibrary ? {
        name: copyDialogLibrary.name, base_video_url: copyDialogLibrary.base_video_url,
        duration_seconds: copyDialogLibrary.duration_seconds ?? 0, format: copyDialogLibrary.format ?? DEFAULT_CLIP_FORMAT, language: copyDialogLibrary.language ?? 'pt-BR',
        start_time: copyDialogLibrary.start_time, end_time: copyDialogLibrary.end_time, source_library_id: copyDialogLibrary.id,
      } : null} />
      <CpmTermsModal open={termsOpen} onOpenChange={next => { if (!next) pendingJoinRef.current = false; setTermsOpen(next); }} version={cpmTermsCurrentVersion} onAccepted={async () => { await refetchMyData(); if (pendingJoinRef.current) { pendingJoinRef.current = false; await performJoin(); } }} />
      {submissionErrorDialog}
    </RankingCampaignPage></Suspense>;
  }

  return (
    <Layout>
      <main className="flex-1 p-4 sm:p-6 lg:p-8 overflow-y-auto">
        <div className="max-w-7xl mx-auto space-y-6">
          {isEnded && <CampaignEndedNotice endDate={campaign.end_date} />}
          <Card className="bg-gradient-to-br from-primary/5 via-card to-card border-border overflow-hidden">
            <CardContent className="p-6 sm:p-8">
              <div className="flex flex-col md:flex-row md:items-start md:gap-6">
                <CampaignBanner
                  src={campaign.banner_url}
                  className="mb-4 rounded-xl border border-border/50 md:order-last md:mb-0 md:w-[38%] md:flex-shrink-0"
                />
                <div className="min-w-0 md:flex-1">
                  <div className="flex items-center gap-2 flex-wrap mb-4">
                    {isEnded && (
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider bg-muted text-muted-foreground border border-border">
                        {t('campaignAudit.status.ended')}
                      </span>
                    )}
                    {isActive && !budgetExhausted && (
                      <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider bg-primary/15 text-primary border border-primary/40">
                        <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse" />
                        {t('campaign.statusAvailable')}
                      </span>
                    )}
                    {budgetExhausted && (
                      <span
                        data-testid="campaign-budget-exhausted-badge"
                        className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider bg-red-500/15 text-red-400 border border-red-500/40"
                      >
                        <AlertTriangle className="w-3 h-3" />
                        {t('campaign.budgetExhausted')}
                      </span>
                    )}
                    {budgetLow && (
                      <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider bg-red-500/15 text-red-400 border border-red-500/40">
                        <AlertTriangle className="w-3 h-3" />
                        {t('campaign.budgetConsumingFast')}
                      </span>
                    )}
                    {isInviteOnlyLocked && (
                      <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider bg-muted text-muted-foreground border border-border/60">
                        <Lock className="w-3 h-3" />
                        {isSelectedLocked
                          ? t('earnPage.selectedOnly.ctaLabel')
                          : t('earnPage.inviteOnly.ctaLabel')}
                      </span>
                    )}
                    {isSelectedOnly && !isSelectedLocked && (
                      <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider bg-primary/15 text-primary border border-primary/40">
                        <Crown className="w-3 h-3" />
                        {t('earnPage.selectedOnly.exclusiveBadge')}
                      </span>
                    )}
                    {isProExclusive && (
                      <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider bg-primary/15 text-primary border border-primary/40">
                        <Crown className="w-3 h-3" />
                        {t('earnPage.inviteOnly.ctaLabel')}
                      </span>
                    )}
                  </div>

                  <h1 className="text-2xl sm:text-3xl font-bold mb-1">
                    <span className="bg-gradient-primary bg-clip-text text-transparent">
                      {campaign.name}
                    </span>
                  </h1>
                  <CampaignCategoryBadges categories={campaign.categories} className="mb-3" />

                  {isInviteOnlyLocked && (
                    <div className="mt-3 flex flex-col sm:flex-row sm:items-center gap-3 rounded-lg border border-border/60 bg-muted/40 p-3 text-sm">
                      <div className="flex items-start gap-2 flex-1">
                        <Lock className="w-4 h-4 mt-0.5 flex-shrink-0 text-muted-foreground" />
                        <div>
                          <p className="font-semibold">
                            {isSelectedLocked
                              ? t('earnPage.selectedOnly.popoverTitle')
                              : t('earnPage.inviteOnly.popoverTitle')}
                          </p>
                          <p className="text-muted-foreground">
                            {isSelectedLocked
                              ? t('earnPage.selectedOnly.popoverText')
                              : t('earnPage.inviteOnly.popoverText')}
                          </p>
                        </div>
                      </div>
                      {/* Upgrade CTA only for the invite lock. A paid plan cannot
                          open a selected-only campaign, so offering it there would
                          sell an upgrade that grants nothing. */}
                      {!isSelectedLocked && (
                        <Button asChild variant="outline" className="font-bold border-primary/30 text-primary gap-2 hover:bg-primary/10 hover:text-primary sm:flex-shrink-0">
                          <RouterLink to="/plans">
                            <Crown className="w-4 h-4" />
                            {t('earnPage.inviteOnly.ctaLabel')}
                          </RouterLink>
                        </Button>
                      )}
                    </div>
                  )}
                  {(campaign.description || t('campaign.subtitle')) && (
                    <div className="prose prose-sm prose-invert max-w-none mb-3 sm:mb-6
                      prose-p:text-muted-foreground prose-p:my-1.5
                      prose-headings:text-foreground prose-headings:font-semibold prose-h1:text-lg prose-h2:text-base prose-h3:text-sm prose-headings:my-2
                      prose-strong:text-foreground prose-em:text-muted-foreground
                      prose-a:text-primary prose-a:no-underline hover:prose-a:underline
                      prose-ul:my-1.5 prose-ol:my-1.5 prose-li:text-muted-foreground prose-li:my-0.5
                      prose-blockquote:text-muted-foreground prose-blockquote:border-border
                      prose-code:text-foreground prose-code:bg-muted/50 prose-code:px-1 prose-code:py-0.5 prose-code:rounded prose-code:before:content-none prose-code:after:content-none
                      prose-hr:border-border prose-hr:my-3">
                      <ReactMarkdown
                        remarkPlugins={[remarkGfm]}
                        components={{
                          // eslint-disable-next-line @typescript-eslint/no-unused-vars
                          a: ({ node: _node, ...props }) => (
                            <a {...props} target="_blank" rel="noopener noreferrer" />
                          ),
                        }}
                      >
                        {campaign.description || t('campaign.subtitle')}
                      </ReactMarkdown>
                    </div>
                  )}

                  {activePlatforms.length > 0 && (
                    <div className="flex items-center gap-2 flex-wrap mb-3 sm:mb-6">
                      <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                        {t('campaign.allowedNetworks')}
                      </span>
                      {activePlatforms.includes('tiktok') && (
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border bg-muted/40 text-muted-foreground border-border">
                          <SiTiktok className="w-3.5 h-3.5" />
                          TikTok
                        </span>
                      )}
                      {activePlatforms.includes('instagram') && (
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border bg-muted/40 text-muted-foreground border-border">
                          <SiInstagram className="w-3.5 h-3.5" />
                          Instagram
                        </span>
                      )}
                      {activePlatforms.includes('youtube') && (
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border bg-muted/40 text-muted-foreground border-border">
                          <SiYoutube className="w-3.5 h-3.5" />
                          YouTube
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* metric cards */}
              <div className={`grid grid-cols-1 ${metricGridColumnsClass} gap-3 mb-3 sm:mb-6`}>
                <div className="bg-background/60 border border-border rounded-xl p-3 sm:p-4">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1">
                    {t('campaign.maxEarningsLabel')}
                  </p>
                  <p className="text-xl font-bold text-primary">
                    {formatCents(campaign.budget_total_cents || 0, campaign.currency)}
                  </p>
                </div>

                <div className="bg-background/60 border border-border rounded-xl p-3 sm:p-4">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1">
                    {t('campaign.rateLabel')}
                  </p>
                  <p className="text-xl font-bold">
                    {formatCents(campaign.cpm_rate_cents, campaign.currency)}
                    <span className="text-sm font-normal text-muted-foreground ml-1">
                      {' / '}{t('campaign.per1kViews')}
                    </span>
                  </p>
                  <p className="text-[11px] text-muted-foreground mt-1">
                    {t('campaign.rateLabelHint', { rate: formatCents(campaign.cpm_rate_cents, campaign.currency) })}
                  </p>
                </div>

                {minViews.kind === 'minimum' ? (
                  <div
                    data-testid="campaign-min-views-card"
                    className="bg-background/60 border border-amber-500/30 rounded-xl p-3 sm:p-4"
                  >
                    <p className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1">
                      <Eye className="w-3 h-3 text-amber-400" />
                      {t('campaign.minViewsLabel')}
                    </p>
                    <p className="text-xl font-bold text-amber-400">
                      {minViews.views.toLocaleString()}
                      <span className="text-sm font-normal text-muted-foreground ml-1">
                        {t('campaign.minViewsPerClipSuffix')}
                      </span>
                    </p>
                    <p className="text-[11px] text-muted-foreground mt-1">
                      {t('campaign.minViewsHint', { views: minViews.views.toLocaleString() })}
                    </p>
                  </div>
                ) : (
                  <div
                    data-testid="campaign-no-min-views-card"
                    className="bg-background/60 border border-emerald-500/30 rounded-xl p-3 sm:p-4"
                  >
                    <p className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1">
                      <Eye className="w-3 h-3 text-emerald-500" />
                      {t('campaign.minViewsLabel')}
                    </p>
                    <p className="text-xl font-bold text-emerald-500">
                      {t('campaign.noMinViews')}
                    </p>
                    <p className="text-[11px] text-muted-foreground mt-1">
                      {t('campaign.noMinViewsHint')}
                    </p>
                  </div>
                )}

                <div className={`bg-background/60 border rounded-xl p-3 sm:p-4 ${
                  budgetExhausted ? 'border-red-500/40' : 'border-border'
                }`}>
                  <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1">
                    {t('campaign.budgetRemainingLabel')}
                  </p>
                  <p className={`text-xl font-bold ${budgetExhausted ? 'text-red-400' : 'text-cyan-400'}`}>
                    {formatCents(campaign.budget_remaining_cents || 0, campaign.currency)}
                  </p>
                  {budgetExhausted && (
                    <p className="text-[11px] text-red-400/90 mt-1">
                      {t('campaign.budgetExhausted')}
                    </p>
                  )}
                </div>

                {maxPayoutPerClipCents > 0 && (
                  <div className="bg-background/60 border border-border rounded-xl p-3 sm:p-4">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1">
                      {t('campaign.clipLimitLabel')}
                    </p>
                    <p className="text-xl font-bold text-primary">
                      {formatCents(maxPayoutPerClipCents, campaign.currency)}
                    </p>
                  </div>
                )}
              </div>

              {(campaign.view_accrual_window_hours ?? 0) > 0 && (
                <p className="text-xs text-muted-foreground mb-3 sm:mb-6">
                  {t('campaign.accrualWindowNote', { hours: campaign.view_accrual_window_hours })}
                </p>
              )}

              {/* Editing assets — files for clippers editing outside the platform */}
              {Array.isArray(campaign.editing_assets) && campaign.editing_assets.length > 0 && (
                <div className="bg-background/60 border border-border rounded-xl p-3 sm:p-4 mb-3 sm:mb-6">
                  <div className="flex items-center gap-2 mb-1">
                    <FolderDown className="w-4 h-4 text-primary" />
                    <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                      {t('campaign.editingAssets.title')}
                    </p>
                  </div>
                  <p className="text-xs text-muted-foreground mb-3">
                    {t('campaign.editingAssets.hint')}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {(campaign.editing_assets as { name: string; url: string; size?: number }[]).map((asset, i) => (
                      <a
                        key={`${asset.url}-${i}`}
                        href={asset.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        download
                        className="group flex items-center gap-2 rounded-lg border border-border bg-muted/30 hover:bg-muted/60 hover:border-primary/50 transition-colors px-3 py-2"
                      >
                        <Download className="w-3.5 h-3.5 text-primary shrink-0" />
                        <span className="text-sm font-medium group-hover:text-primary transition-colors">
                          {asset.name}
                        </span>
                        {asset.size != null && (
                          <span className="text-xs text-muted-foreground">{formatFilesize(asset.size)}</span>
                        )}
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {/* Budget progress bar */}
              <div className="mb-3 sm:mb-6 space-y-2">
                <CampaignBudgetBar
                  totalCents={campaign.budget_total_cents}
                  spentCents={spent}
                  pendingCents={campaign.budget_pending_cents}
                  currency={campaign.currency}
                  size="md"
                  showLegend
                />
                {budgetExhausted && (
                  <div
                    data-testid="campaign-budget-exhausted-banner"
                    className="flex items-start gap-2 rounded-md border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm leading-snug text-red-300"
                  >
                    <AlertTriangle
                      aria-hidden="true"
                      focusable="false"
                      className="mt-0.5 h-4 w-4 shrink-0"
                    />
                    <span>{t('campaign.budgetExhaustedBanner')}</span>
                  </div>
                )}
              </div>

              {!hasAcceptedCpmTerms ? (
                <div className="flex items-center gap-4 flex-wrap">
                  <Button
                    size="lg"
                    className="h-12 px-8 font-semibold"
                    onClick={() => {
                      // "Participate" == accept terms + join. Queue the join so
                      // the submission form appears right after terms accept,
                      // matching the Join panel's visibility conditions.
                      if (isActive && !isJoined && !isInviteOnlyLocked) {
                        pendingJoinRef.current = true;
                      }
                      setTermsOpen(true);
                    }}
                  >
                    {t('campaign.participateCta')}
                  </Button>
                  <p className="text-sm text-muted-foreground">{t('campaign.participateSubtext')}</p>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="flex items-center gap-3 flex-wrap">
                    <Button
                      variant="gradient"
                      size="lg"
                      className="h-12 px-8 font-semibold gap-2"
                      onClick={() => document.getElementById('participating-videos')?.scrollIntoView({ behavior: 'smooth' })}
                    >
                      <Scissors className="w-4 h-4" />
                      {t('campaign.createViralClips')}
                    </Button>
                    <Button
                      variant="outline-gradient"
                      size="lg"
                      className="h-12 px-8 font-semibold gap-2"
                      onClick={() => document.getElementById('submission-form')?.scrollIntoView({ behavior: 'smooth' })}
                    >
                      <Link2 className="w-4 h-4" />
                      {t('campaign.sendMyClips')}
                    </Button>
                  </div>
                  {campaign.required_hashtags && (
                    <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
                      <AlertTriangle className="w-4 h-4 text-amber-400 mt-0.5 shrink-0" />
                      <p className="text-xs text-amber-300/80">
                        {t('campaign.participateTagsReminder')}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          <CampaignWalletCard />

          {isActive && !isJoined && !isInviteOnlyLocked ? (
            <Card className="p-5 space-y-3">
              <h3 className="font-bold text-base">{t('campaign.join.title')}</h3>
              <p className="text-sm text-muted-foreground">{t('campaign.join.description')}</p>
              {isFreeLimitBlocked ? (
                <>
                  <Button asChild variant="outline" className="w-full font-bold border-primary/30 text-primary gap-2 hover:bg-primary/10 hover:text-primary">
                    <RouterLink to="/plans">
                      <Crown className="w-4 h-4" />
                      {t('campaign.join.upgradeCta')}
                    </RouterLink>
                  </Button>
                  <p className="text-xs text-muted-foreground text-center">
                    {t('campaign.join.freeLimitNote')}
                  </p>
                </>
              ) : (
                <Button onClick={onJoin} disabled={joining} className="w-full font-bold">
                  {joining ? t('campaign.join.joining') : t('campaign.join.cta')}
                </Button>
              )}
            </Card>
          ) : isActive ? (
            <>
              {channelApprovalSection}

          {clipSubmissionForm}
            </>
          ) : null}

          <section className="space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h2 className="text-xl font-semibold inline-flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-primary" />
                {t('campaign.submissions.provisionalEarnings')}
              </h2>
              <div className="flex items-center gap-3">
                {disapprovedCount > 0 && (
                  <button
                    type="button"
                    onClick={() => setHideDisapproved((v) => !v)}
                    className="text-xs text-muted-foreground hover:text-foreground underline underline-offset-2"
                  >
                    {hideDisapproved
                      ? t('campaign.submissions.showDisapproved', { count: disapprovedCount })
                      : t('campaign.submissions.hideDisapproved', { count: disapprovedCount })}
                  </button>
                )}
                <span className="text-xs text-muted-foreground">
                  {t('campaign.submissions.calculatedAt', {
                    rate: formatCents(campaign.cpm_rate_cents, campaign.currency),
                  })}
                </span>
              </div>
            </div>

            {submissions.length > 0 && (
              <div
                data-testid="campaign-earnings-summary"
                className="grid grid-cols-2 lg:grid-cols-4 gap-3"
              >
                <div className="bg-card border border-primary/30 rounded-xl p-3 sm:p-4">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1">
                    {t('campaign.submissions.summary.confirmed')}
                  </p>
                  <p className="text-xl font-bold text-primary">
                    {formatCents(earningsSummary.confirmedCents, campaign.currency)}
                  </p>
                </div>
                <div className="bg-card border border-amber-500/30 rounded-xl p-3 sm:p-4">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1">
                    {t('campaign.submissions.summary.potential')}
                  </p>
                  <p className="text-xl font-bold text-amber-400">
                    {formatCents(earningsSummary.potentialCents, campaign.currency)}
                  </p>
                </div>
                <div className="bg-card border border-border rounded-xl p-3 sm:p-4">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1">
                    {t('campaign.submissions.summary.views')}
                  </p>
                  <p className="text-xl font-bold">
                    {earningsSummary.totalViews.toLocaleString()}
                  </p>
                </div>
                <div className="bg-card border border-border rounded-xl p-3 sm:p-4">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1">
                    {t('campaign.submissions.summary.clips')}
                  </p>
                  <p className="text-xl font-bold">{earningsSummary.clipCount}</p>
                </div>
              </div>
            )}

            {isJoined && isActive && (
              <div className="flex justify-end">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setLeaveConfirmOpen(true)}
                  disabled={leaving}
                  className="text-muted-foreground hover:text-destructive"
                >
                  {t('campaign.leave.cta')}
                </Button>
              </div>
            )}

            {submissions.length === 0 ? (
              <p className="text-muted-foreground">{t('campaign.submissions.empty')}</p>
            ) : visibleSubmissions.length === 0 ? (
              <p className="text-muted-foreground">{t('campaign.submissions.allDisapprovedHidden')}</p>
            ) : (
              <div className="space-y-6">
                {platformGroups.map((platform) => {
                  const platformSubs = submissionsByPlatform[platform];
                  return (
                    <CampaignSubmissionTable key={platform} platform={platform} count={platformSubs.length} columns={[
                      { label: t('campaign.submissions.clipLink') },
                      { label: t('campaign.submissions.verdict') },
                      { label: t('campaign.submissions.feedback') },
                      { label: t('campaign.submissions.views'), className: 'text-right' },
                      { label: t('campaign.submissions.earningsProgress') },
                    ]}>
                            {platformSubs.map((s: any) => {
                              const views = Number(
                                s.payable_views ?? s.latest_views ?? s.last_counted_views ?? 0,
                              );
                              const verdictKey = getSubmissionVerdictKey(s);
                              const earnings = resolveSubmissionEarningsDisplay({
                                verdict: verdictKey,
                                views,
                                accumulatedPayoutCents: s.accumulated_payout_cents,
                                campaignCpmRateCents: campaign.cpm_rate_cents,
                                overrideCents: s.cpm_rate_override_cents,
                                minViewsThreshold: campaign.min_views_threshold,
                                maxPayoutPerClipCents: campaign.max_payout_per_clip_cents,
                              });
                              const earnedCents = earnings.cents;
                              const perClipCap = campaign.max_payout_per_clip_cents || null;
                              const earningsHint = resolveSubmissionEarningsHint({
                                earningsKind: earnings.kind,
                                earnedCents,
                                maxPayoutPerClipCents: perClipCap,
                                viewsRemainingToThreshold: earnings.viewsRemainingToThreshold,
                                minViewsThreshold,
                                remainingBudgetCents: campaign.budget_remaining_cents,
                                submittedAt: s.createdAt,
                                windowHours: campaign.view_accrual_window_hours,
                                now: new Date(),
                              });
                              const progressPct = perClipCap
                                ? Math.min(100, (earnedCents / perClipCap) * 100)
                                : 0;
                              const showPendingBudgetExhaustedWarning = shouldShowPendingBudgetExhaustedWarning({
                                verdict: verdictKey,
                                remainingBudgetCents: campaign.budget_remaining_cents,
                              });
                              const submittedAt = formatDateTime(s.createdAt).split(' ')[0];
                              const feedback = getSubmissionFeedback(s, campaign);
                              const isDuplicateLink = feedback.kind === 'disqualified'
                                && feedback.reasonCode === 'suspected_duplicate_resolved';

                              return (
                                <TableRow
                                  key={s.id}
                                  id={`campaign-submission-${s.id}`}
                                  tabIndex={-1}
                                  data-highlighted={submissionToReveal?.id === s.id ? 'true' : undefined}
                                  className={`${s.disqualified ? 'opacity-60' : ''} scroll-mt-16 data-[highlighted=true]:bg-primary/15 data-[highlighted=true]:outline data-[highlighted=true]:outline-2 data-[highlighted=true]:outline-primary data-[highlighted=true]:-outline-offset-2`}
                                >
                                  <TableCell className={`max-w-xs border-l-4 ${VERDICT_ACCENT_BORDER[verdictKey]}`}>
                                    <a
                                      href={s.url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="flex items-center gap-1 text-sm text-blue-400 hover:underline truncate"
                                      title={s.url}
                                    >
                                      <ExternalLink className="w-3 h-3 shrink-0" />
                                      <span className="truncate">{s.url}</span>
                                    </a>
                                    {submittedAt && (
                                      <span className="flex items-center gap-1 text-[10px] text-muted-foreground mt-0.5">
                                        {submittedAt}
                                        {isDuplicateLink && (
                                          <span className="text-red-400">
                                            · {t('campaign.submissions.duplicateLinkTag')}
                                          </span>
                                        )}
                                      </span>
                                    )}
                                  </TableCell>

                                  <TableCell className="whitespace-nowrap">
                                    {(() => {
                                      const classes = verdictKey === 'disqualified'
                                        ? 'bg-red-500/20 text-red-400 border-red-500/40'
                                        : verdictKey === 'approved'
                                          ? 'bg-primary/15 text-primary border-primary/40'
                                          : verdictKey === 'disapproved'
                                            ? 'bg-red-500/15 text-red-400 border-red-500/40'
                                            : verdictKey === 'pre_approved'
                                              ? 'bg-sky-500/10 text-sky-400 border-sky-500/40'
                                              : 'bg-amber-500/10 text-amber-400 border-amber-500/40';
                                      const badge = (
                                        <span className={`inline-flex items-center px-2 py-1 rounded text-[10px] font-bold uppercase tracking-wide border ${classes}`}>
                                          {t(`campaign.submissions.approvalStates.${verdictKey}`)}
                                        </span>
                                      );
                                      if (verdictKey === 'pending') {
                                        return (
                                          <Tooltip>
                                            <TooltipTrigger asChild>{badge}</TooltipTrigger>
                                            <TooltipContent className="max-w-[200px] text-xs text-center">
                                              {t('campaign.submissions.approvalStates.pendingTooltip')}
                                            </TooltipContent>
                                          </Tooltip>
                                        );
                                      }
                                      if (verdictKey === 'pre_approved') {
                                        return (
                                          <Tooltip>
                                            <TooltipTrigger asChild>{badge}</TooltipTrigger>
                                            <TooltipContent className="max-w-[220px] text-xs text-center">
                                              {t('campaign.submissions.approvalStates.preApprovedTooltip')}
                                            </TooltipContent>
                                          </Tooltip>
                                        );
                                      }
                                      return badge;
                                    })()}
                                    {verdictKey === 'pre_approved' && (
                                      <div className="mt-1 space-y-0.5">
                                        <span className="block text-[10px] leading-snug text-muted-foreground">
                                          {t('campaign.submissions.preApproval.pendingAuditNote')}
                                        </span>
                                        {typeof s.estimated_payout_cents === 'number' && (
                                          <span className="block text-[10px] font-semibold leading-snug text-sky-400">
                                            {t('campaign.submissions.preApproval.estimatedPayout', {
                                              amount: formatCents(s.estimated_payout_cents, campaign.currency),
                                            })}
                                          </span>
                                        )}
                                      </div>
                                    )}
                                    {s.suspected_duplicate && !s.disqualified && (
                                      <Badge variant="outline" className="text-amber-500 border-amber-500/40 text-[10px] ml-1">
                                        {t('campaign.submission.checkingDuplicate')}
                                      </Badge>
                                    )}
                                  </TableCell>

                                  <TableCell>
                                    {(() => {
                                      const renderText = (raw: string) => {
                                        const codeKey = `campaign.submissions.feedbackCodes.${raw}`;
                                        const translated = t(codeKey);
                                        return translated === codeKey ? raw : translated;
                                      };
                                      const appealUi = resolveAppealRowUi({
                                        campaignStatus: campaign.status,
                                        submission: s,
                                        appeal: s.appeal ?? null,
                                        now: new Date(),
                                      });
                                      const originalReason = s.approval_reason
                                        ? renderText(s.approval_reason)
                                        : '';
                                      const baseBox = 'w-[340px] max-h-60 overflow-y-auto whitespace-pre-line break-words text-sm leading-snug pr-1';
                                      const observation = s.admin_observation?.trim();
                                      const metricsErrorKey = mapOfficialMetricsError(s.metrics_error);
                                      const metricsError = metricsErrorKey ? (
                                        <p className="mt-1 text-amber-400">{t(metricsErrorKey)}</p>
                                      ) : null;
                                      const observationBlock = observation ? (
                                        <p className="mt-1 text-amber-300">
                                          <span className="text-muted-foreground">{t('campaign.submissions.adminObservationLabel')}</span>{' '}
                                          {observation}
                                        </p>
                                      ) : null;
                                      let existing: ReactNode;
                                      if (feedback.kind === 'disqualified') {
                                        existing = (
                                          <div className={`${baseBox} text-red-400`}>
                                            {feedback.reasonCode
                                              ? renderText(feedback.reasonCode)
                                              : t('campaign.submissions.disqualifiedGenericReason')}
                                            {observationBlock}
                                            {metricsError}
                                          </div>
                                        );
                                      } else if (feedback.kind === 'approval') {
                                        existing = (
                                          <div className={`${baseBox} ${feedback.muted ? 'text-muted-foreground' : 'text-red-400'}`}>
                                            {renderText(feedback.reasonCode)}
                                            {feedback.captionTokens && <CaptionTokensNote captionTokens={feedback.captionTokens} />}
                                            {observationBlock}
                                            {metricsError}
                                          </div>
                                        );
                                      } else if (feedback.kind === 'underReview') {
                                        existing = (
                                          <div className={`${baseBox} text-amber-500`}>
                                            {t('campaign.submissions.underReviewReason')}
                                            {observationBlock}
                                            {metricsError}
                                          </div>
                                        );
                                      } else if (feedback.kind === 'rulesCheckFailures') {
                                        existing = (
                                          <div className={`${baseBox} text-amber-400`}>
                                            <ul className="list-disc pl-4 space-y-0.5">
                                              {feedback.codes.map((code) => (
                                                <li key={code}>{renderText(code)}</li>
                                              ))}
                                            </ul>
                                            {feedback.captionTokens && <CaptionTokensNote captionTokens={feedback.captionTokens} />}
                                            {observationBlock}
                                            {metricsError}
                                          </div>
                                        );
                                      } else {
                                        existing = (
                                          <div className={`${baseBox} text-sm text-muted-foreground`}>
                                            {metricsError || '—'}
                                            {observationBlock}
                                          </div>
                                        );
                                      }
                                      const rejectionImages = s.admin_approval === 'disapproved'
                                        ? filterPublicReviewImages(s.rejection_images)
                                        : [];
                                      if (rejectionImages.length > 0) {
                                        existing = (
                                          <div className="space-y-1.5">
                                            {existing}
                                            <ReviewImagesStrip images={rejectionImages} />
                                          </div>
                                        );
                                      }
                                      if (appealUi.kind === 'pending') {
                                        return (
                                          <div className={`${baseBox} space-y-1.5`}>
                                            <span className="inline-flex items-center px-2 py-1 rounded text-[10px] font-bold uppercase tracking-wide border bg-amber-500/10 text-amber-400 border-amber-500/40">
                                              {t('campaign.submissions.appeal.pendingBadge')}
                                            </span>
                                            <p className="text-amber-400">
                                              {t('campaign.submissions.appeal.pendingCopy')}
                                            </p>
                                            {appealUi.argument ? (
                                              <p className="text-muted-foreground">{appealUi.argument}</p>
                                            ) : null}
                                            {appealUi.evidenceImageUrls.length > 0 ? (
                                              <div className="flex flex-wrap gap-2 pt-1">
                                                {appealUi.evidenceImageUrls.map((url) => (
                                                  <a
                                                    key={url}
                                                    href={url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="block h-16 w-16 overflow-hidden rounded-md border border-border"
                                                  >
                                                    <img
                                                      src={url}
                                                      alt=""
                                                      className="h-full w-full object-cover"
                                                    />
                                                  </a>
                                                ))}
                                              </div>
                                            ) : null}
                                          </div>
                                        );
                                      }
                                      if (appealUi.kind === 'kept') {
                                        return (
                                          <div className="space-y-1.5">
                                            {existing}
                                            <p className="w-[340px] text-sm leading-snug text-amber-300 whitespace-pre-line break-words">
                                              <span className="text-muted-foreground">
                                                {t('campaign.submissions.appeal.keptReplyLabel')}
                                              </span>
                                              {' '}
                                              {appealUi.reply}
                                            </p>
                                            <ReviewImagesStrip images={appealUi.reviewerImages} />
                                          </div>
                                        );
                                      }
                                      if (appealUi.kind === 'needsMoreInfo') {
                                        return (
                                          <div className="space-y-2">
                                            {existing}
                                            <p className="w-[340px] text-sm leading-snug text-amber-300 whitespace-pre-line break-words">
                                              <span className="text-muted-foreground">
                                                {t('campaign.submissions.appeal.keptReplyLabel')}
                                              </span>
                                              {' '}
                                              {appealUi.reply}
                                            </p>
                                            <ReviewImagesStrip images={appealUi.reviewerImages} />
                                            <p className="text-amber-400">
                                              {t('campaign.submissions.appeal.needsMoreCopy')}
                                            </p>
                                            {appealUi.argument ? (
                                              <p className="text-muted-foreground">{appealUi.argument}</p>
                                            ) : null}
                                            {appealUi.evidenceImageUrls.length > 0 ? (
                                              <div className="flex flex-wrap gap-2 pt-1">
                                                {appealUi.evidenceImageUrls.map((url) => (
                                                  <a
                                                    key={url}
                                                    href={url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="block h-16 w-16 overflow-hidden rounded-md border border-border"
                                                  >
                                                    <img
                                                      src={url}
                                                      alt=""
                                                      className="h-full w-full object-cover"
                                                    />
                                                  </a>
                                                ))}
                                              </div>
                                            ) : null}
                                            {!appealUi.followUpDisabled ? (
                                              <Button
                                                type="button"
                                                size="sm"
                                                variant="outline"
                                                data-testid="appeal-follow-up-button"
                                                onClick={() => setAppealTarget({
                                                  id: s.id,
                                                  originalReason,
                                                  mode: 'followUp',
                                                  appealId: s.appeal?.id,
                                                  auditorReply: appealUi.reply,
                                                })}
                                              >
                                                {t('campaign.submissions.appeal.sendMoreData')}
                                              </Button>
                                            ) : null}
                                          </div>
                                        );
                                      }
                                      if (appealUi.kind === 'accepted') {
                                        return (
                                          <div className="space-y-1.5">
                                            {existing}
                                            <p className="w-[340px] text-sm leading-snug text-amber-300 whitespace-pre-line break-words">
                                              <span className="text-muted-foreground">
                                                {t('campaign.submissions.appeal.acceptedReplyLabel')}
                                              </span>
                                              {' '}
                                              {appealUi.reply}
                                            </p>
                                          </div>
                                        );
                                      }
                                      if (appealUi.kind === 'contest') {
                                        return (
                                          <div className="space-y-2">
                                            {existing}
                                            <Button
                                              type="button"
                                              size="sm"
                                              variant="outline"
                                              data-testid="appeal-contest-button"
                                              onClick={() => setAppealTarget({
                                                id: s.id,
                                                originalReason,
                                                mode: 'contest',
                                              })}
                                            >
                                              {t('campaign.submissions.appeal.contest')}
                                            </Button>
                                          </div>
                                        );
                                      }
                                      return existing;
                                    })()}
                                  </TableCell>

                                  <TableCell className="text-right whitespace-nowrap">
                                    <div className="flex flex-col items-end gap-0.5">
                                      <span className="inline-flex items-center gap-1 text-sm justify-end">
                                        <Eye className="w-3.5 h-3.5 text-muted-foreground" />
                                        {views.toLocaleString()}
                                      </span>
                                      {minViewsThreshold > 0 && (
                                        <span
                                          data-testid={`min-views-${s.id}`}
                                          className={`text-[10px] ${
                                            views < minViewsThreshold
                                              ? 'text-amber-400/80'
                                              : 'text-muted-foreground'
                                          }`}
                                        >
                                          {views < minViewsThreshold
                                            ? t('campaign.submissions.viewsUntilMin', {
                                              count: minViewsThreshold - views,
                                              threshold: minViewsThreshold.toLocaleString(),
                                            })
                                            : t('campaign.minViewsShort', {
                                              count: minViewsThreshold.toLocaleString(),
                                            })}
                                        </span>
                                      )}
                                    </div>
                                  </TableCell>

                                  <TableCell className="min-w-[180px]">
                                    <div className="space-y-1">
                                      {Number.isInteger(s.cpm_rate_override_cents)
                                        && s.cpm_rate_override_cents !== campaign.cpm_rate_cents && (
                                        <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-sky-500/15 text-sky-400 text-[10px] font-bold uppercase tracking-wide">
                                          {t('campaign.submissions.customRate', {
                                            rate: formatCents(s.cpm_rate_override_cents, campaign.currency),
                                          })}
                                        </span>
                                      )}
                                      {showPendingBudgetExhaustedWarning ? (
                                        <div
                                          data-testid={`pending-budget-exhausted-${s.id}`}
                                          className="flex items-start gap-1.5 rounded-md border border-amber-500/30 bg-amber-500/10 px-2 py-1.5 text-[11px] leading-snug text-amber-300"
                                        >
                                          <AlertTriangle
                                            aria-hidden="true"
                                            focusable="false"
                                            className="mt-0.5 h-3.5 w-3.5 shrink-0"
                                          />
                                          <span>{t('campaign.submissions.budgetExhaustedPendingWarning')}</span>
                                        </div>
                                      ) : (
                                        <>
                                          <div className="flex items-center justify-between gap-2">
                                            {earnings.kind === 'potential' ? (
                                              <Tooltip>
                                                <TooltipTrigger asChild>
                                                  <span
                                                    data-testid={`potential-earnings-${s.id}`}
                                                    className="inline-flex items-baseline gap-1.5"
                                                  >
                                                    <span className="font-bold text-sm text-amber-400">
                                                      {formatCents(earnedCents, campaign.currency)}
                                                    </span>
                                                    <span className="text-[10px] font-semibold uppercase tracking-wide text-amber-400/80">
                                                      {t('campaign.submissions.potentialLabel')}
                                                    </span>
                                                  </span>
                                                </TooltipTrigger>
                                                <TooltipContent className="max-w-[220px] text-xs">
                                                  {t('campaign.submissions.potentialTooltip')}
                                                </TooltipContent>
                                              </Tooltip>
                                            ) : (
                                              <span className="font-bold text-sm">
                                                {formatCents(earnedCents, campaign.currency)}
                                              </span>
                                            )}
                                            {earningsHint.kind === 'limitReached' ? (
                                              <span className="text-[10px] font-bold tracking-wide px-1.5 py-0.5 rounded bg-primary/20 text-primary">
                                                {t('campaign.submissions.limitReached').toUpperCase()}
                                              </span>
                                            ) : earningsHint.kind === 'accrualWindowClosed' ? (
                                              <span
                                                data-testid={`accrual-window-closed-${s.id}`}
                                                className="text-[10px] text-muted-foreground"
                                              >
                                                {t('campaign.submissions.accrualWindowClosed', {
                                                  hours: earningsHint.windowHours,
                                                })}
                                              </span>
                                            ) : earningsHint.kind === 'missedThresholdInWindow' ? (
                                              <span
                                                data-testid={`missed-threshold-${s.id}`}
                                                className="text-[10px] text-amber-400/80"
                                              >
                                                {t('campaign.submissions.missedThresholdInWindow', {
                                                  threshold: earningsHint.threshold.toLocaleString(),
                                                  hours: earningsHint.windowHours,
                                                })}
                                              </span>
                                            ) : earningsHint.kind === 'viewsRemainingToEarn' ? (
                                              <span
                                                data-testid={`views-remaining-${s.id}`}
                                                className="text-[10px] text-amber-400/80"
                                              >
                                                {t('campaign.submissions.viewsRemainingToEarn', {
                                                  count: earningsHint.count,
                                                  threshold: earningsHint.threshold.toLocaleString(),
                                                })}
                                              </span>
                                            ) : earningsHint.kind === 'remainingToLimit' ? (
                                              <span className="text-[10px] text-muted-foreground">
                                                {t('campaign.submissions.remainingToLimit', {
                                                  amount: formatCents(
                                                    earningsHint.remainingCents,
                                                    campaign.currency,
                                                  ),
                                                })}
                                              </span>
                                            ) : null}
                                          </div>
                                          {earningsHint.kind === 'budgetExhausted' && (
                                            <div
                                              data-testid={`budget-exhausted-approved-${s.id}`}
                                              className="flex items-start gap-1.5 rounded-md border border-amber-500/30 bg-amber-500/10 px-2 py-1.5 text-[11px] leading-snug text-amber-300"
                                            >
                                              <AlertTriangle
                                                aria-hidden="true"
                                                focusable="false"
                                                className="mt-0.5 h-3.5 w-3.5 shrink-0"
                                              />
                                              <span>
                                                {t('campaign.submissions.budgetExhaustedApprovedWarning')}
                                              </span>
                                            </div>
                                          )}
                                          {perClipCap ? (
                                            <Progress
                                              value={progressPct}
                                              className="h-1.5"
                                              indicatorClassName={
                                                earnings.kind === 'potential' ? 'bg-amber-400' : undefined
                                              }
                                            />
                                          ) : (
                                            <div className="h-1.5 rounded bg-muted" />
                                          )}
                                        </>
                                      )}
                                    </div>
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                    </CampaignSubmissionTable>
                  );
                })}
              </div>
            )}

            {submissions.length > 0 && (
              <p className="text-xs text-muted-foreground italic text-center pt-2">
                &ldquo;{t('campaign.submissions.disclaimer')}&rdquo;
              </p>
            )}
          </section>

          {/* Link FAQ de pagamento */}
          <div className="flex justify-end">
            <button
              onClick={() => navigate('/earn#payment-faq')}
              className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-primary transition-colors"
            >
              <HelpCircle className="w-3.5 h-3.5" />
              {t('campaign.submit.paymentFaqLink')}
            </button>
          </div>

          <div id="participating-videos">
            <ParticipatingVideosSection
              libraries={participatingLibraries}
              campaignChannelIds={(campaign.youtube_channels || []).map(
                (c: any) => c.channel_id,
              )}
              focusLibraryId={focusLibraryId}
              onGenerateClips={openCopyDialog}
              onRemoveVideo={onRemoveVideo}
            />
            <RemoveCampaignVideoDialog
              campaignId={campaign.id}
              video={videoToRemove}
              onOpenChange={(open) => { if (!open) setVideoToRemove(null); }}
              onRemoved={() => { void refetch(); }}
            />
          </div>

          <section className="space-y-4">
            <div>
              <h2 className="text-xl font-semibold">{t('campaign.howItWorks.title')}</h2>
              <p className="text-sm text-muted-foreground mt-0.5">{t('campaign.howItWorks.subtitle')}</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-5 gap-2">
              {HOW_IT_WORKS_STEPS.map(({ icon: Icon, key }, i) => (
                <div key={key} className="flex sm:flex-col items-center sm:items-start gap-3 sm:gap-2 relative">
                  {i < HOW_IT_WORKS_STEPS.length - 1 && (
                    <ChevronRight className="hidden sm:block absolute -right-1 top-4 w-4 h-4 text-muted-foreground z-10" />
                  )}
                  <div className="bg-card border border-border rounded-xl p-4 flex sm:flex-col items-center sm:items-start gap-3 w-full">
                    <div className="flex items-center gap-2 shrink-0">
                      <span className="w-6 h-6 rounded-full bg-primary/20 text-primary text-xs font-bold flex items-center justify-center shrink-0">
                        {i + 1}
                      </span>
                      <div className="w-8 h-8 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center">
                        <Icon className="w-4 h-4 text-primary" />
                      </div>
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold leading-tight">
                        {t(`campaign.howItWorks.steps.${key}.title`)}
                      </p>
                      <p className="text-xs text-muted-foreground mt-0.5 leading-snug">
                        {t(`campaign.howItWorks.steps.${key}.description`)}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section className="space-y-3">
            <div className="flex items-baseline gap-3 flex-wrap">
              <h2 className="text-xl font-semibold">{t('campaign.rules.title')}</h2>
              <span className="text-sm text-muted-foreground">{t('campaign.rules.readCarefully')}</span>
            </div>
            <Card className="bg-card border-border">
              <CardContent className="p-0">
                <Accordion type="multiple" defaultValue={RULES_DEFAULT_OPEN} className="divide-y divide-border">
                  <AccordionItem value="allowed" className="border-none px-5">
                    <AccordionTrigger className="hover:no-underline py-4">
                      <div className="flex items-center gap-3">
                        <span className="text-xs font-bold text-muted-foreground w-4">1</span>
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-bold uppercase tracking-wider bg-primary/15 text-primary border border-primary/30">
                          <CheckCircle2 className="w-3 h-3" />
                          {t('campaign.rules.authorizedBadge')}
                        </span>
                        <span className="font-semibold text-sm">{t('campaign.rules.authorizedTitle')}</span>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="px-7">
                      {rulesAllowed.length === 0 ? (
                        <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                          {campaign.rules_allowed || '—'}
                        </p>
                      ) : (
                        <ul className="space-y-2">
                          {rulesAllowed.map((rule, i) => (
                            <li key={i} className="text-sm text-muted-foreground flex items-start gap-2">
                              <span className="text-primary mt-1.5 shrink-0">•</span>
                              {rule}
                            </li>
                          ))}
                        </ul>
                      )}
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="forbidden" className="border-none px-5">
                    <AccordionTrigger className="hover:no-underline py-4">
                      <div className="flex items-center gap-3">
                        <span className="text-xs font-bold text-muted-foreground w-4">2</span>
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-bold uppercase tracking-wider bg-red-500/15 text-red-400 border border-red-500/30">
                          <XCircle className="w-3 h-3" />
                          {t('campaign.rules.forbiddenBadge')}
                        </span>
                        <span className="font-semibold text-sm">{t('campaign.rules.forbiddenTitle')}</span>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="px-7">
                      {rulesForbidden.length === 0 ? (
                        <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                          {campaign.rules_forbidden || '—'}
                        </p>
                      ) : (
                        <ul className="space-y-2">
                          {rulesForbidden.map((rule, i) => (
                            <li key={i} className="text-sm text-muted-foreground flex items-start gap-2">
                              <span className="text-red-500 mt-1.5 shrink-0">•</span>
                              {rule}
                            </li>
                          ))}
                        </ul>
                      )}
                    </AccordionContent>
                  </AccordionItem>

                  {hasPunishments && (
                    <AccordionItem value="punishments" className="border-none px-5">
                      <AccordionTrigger className="hover:no-underline py-4">
                        <div className="flex items-center gap-3">
                          <span className="text-xs font-bold text-muted-foreground w-4">3</span>
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-bold uppercase tracking-wider bg-orange-500/15 text-orange-400 border border-orange-500/30">
                            <ShieldAlert className="w-3 h-3" />
                            {t('campaign.rules.punishmentsBadge')}
                          </span>
                          <span className="font-semibold text-sm">{t('campaign.rules.punishmentsTitle')}</span>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent className="px-7 space-y-4">
                        {hasAttention && (
                          <div>
                            <p className="text-xs font-bold uppercase tracking-wider text-amber-400 mb-1">
                              {t('campaign.rules.attention')}
                            </p>
                            <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                              {campaign.rules_attention}
                            </p>
                          </div>
                        )}
                        {hasWarning && (
                          <div>
                            <p className="text-xs font-bold uppercase tracking-wider text-orange-400 mb-1">
                              {t('campaign.rules.warning')}
                            </p>
                            <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                              {campaign.rules_warning}
                            </p>
                          </div>
                        )}
                      </AccordionContent>
                    </AccordionItem>
                  )}

                  {hasLayoutModels && (
                    <AccordionItem value="layout-models" className="border-none px-5">
                      <AccordionTrigger className="hover:no-underline py-4">
                        <div className="flex items-center gap-3">
                          <span className="text-xs font-bold text-muted-foreground w-4">
                            {hasPunishments ? '4' : '3'}
                          </span>
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-bold uppercase tracking-wider bg-blue-500/15 text-blue-400 border border-blue-500/30">
                            <LayoutTemplate className="w-3 h-3" />
                            {t('campaign.rules.layoutModelsBadge')}
                          </span>
                          <span className="font-semibold text-sm">{t('campaign.rules.layoutModelsTitle')}</span>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent className="px-7">
                        <CampaignLayoutModelsGuide />
                      </AccordionContent>
                    </AccordionItem>
                  )}

                </Accordion>
              </CardContent>
            </Card>
          </section>

          {(campaign.required_hashtags || campaign.required_mention || captionExamples.length > 0) && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {requiredMarkup}

              {(captionExamples.length > 0 || captionTip) && (
                <Card className="bg-card border-border">
                  <CardContent className="p-5">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-9 h-9 rounded-lg bg-amber-500/10 border border-amber-500/30 flex items-center justify-center">
                        <Lightbulb className="w-5 h-5 text-amber-400" />
                      </div>
                      <h3 className="font-bold text-lg">
                        {t('campaign.captionExamplesTitle')}
                      </h3>
                    </div>
                    <div className="space-y-5">
                      {captionExamples.map((example, i) => {
                        const badgeClass = 'bg-amber-500/10 text-amber-400 border-amber-500/30';
                        return (
                          <div key={i} className="space-y-2">
                            {example.header && (
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-bold uppercase tracking-wider border ${badgeClass}`}>
                                {example.header}
                              </span>
                            )}
                            <div className="bg-background rounded-lg p-3">
                              <p className="text-sm italic whitespace-pre-wrap leading-relaxed">
                                {example.body}
                              </p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    {captionTip && (
                      <div className="mt-5 flex items-start gap-2.5 p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
                        <Lightbulb className="w-4 h-4 text-amber-400 mt-0.5 shrink-0" />
                        <p className="text-sm text-amber-200/80 leading-relaxed">{captionTip}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          <AppealContestDialog
            open={!!appealTarget}
            onOpenChange={(open) => { if (!open && !appealing) setAppealTarget(null); }}
            originalReason={appealTarget?.originalReason ?? ''}
            auditorReply={appealTarget?.auditorReply}
            mode={appealTarget?.mode ?? 'contest'}
            loading={appealing}
            onSubmit={handleAppealSubmit}
          />

          <Dialog open={leaveConfirmOpen} onOpenChange={(open) => { if (!leaving) setLeaveConfirmOpen(open); }}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{t('campaign.leave.confirmTitle')}</DialogTitle>
                <DialogDescription>{t('campaign.leave.confirmDescription')}</DialogDescription>
              </DialogHeader>
              {(leaveImpact.pendingCount > 0 || leaveImpact.approvedCount > 0) && (
                <ul className="space-y-2 text-sm">
                  {leaveImpact.pendingCount > 0 && (
                    <li className="rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-amber-200">
                      {t('campaign.leave.pendingRejected', { count: leaveImpact.pendingCount })}
                    </li>
                  )}
                  {leaveImpact.approvedCount > 0 && (
                    <li className="rounded-md border border-emerald-500/30 bg-emerald-500/10 px-3 py-2 text-emerald-200">
                      {t('campaign.leave.approvedKept', { count: leaveImpact.approvedCount })}
                    </li>
                  )}
                </ul>
              )}
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setLeaveConfirmOpen(false)}
                  disabled={leaving}
                >
                  {t('campaign.leave.cancel')}
                </Button>
                <Button
                  type="button"
                  variant="destructive"
                  onClick={onLeave}
                  disabled={leaving}
                  data-testid="campaign-leave-confirm"
                >
                  {leaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {t('campaign.leave.confirm')}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {submissionErrorDialog}

          <CopyLibraryDialog
            isOpen={!!copyDialogLibrary}
            onClose={() => setCopyDialogLibrary(null)}
            title={t('library.generateAIClips')}
            isPaidPlan={isPaidPlan}
            cpmUnlocked={cpmUnlocked}
            library={copyDialogLibrary ? {
              name: copyDialogLibrary.name,
              base_video_url: copyDialogLibrary.base_video_url,
              duration_seconds: copyDialogLibrary.duration_seconds ?? 0,
              format: copyDialogLibrary.format ?? DEFAULT_CLIP_FORMAT,
              language: copyDialogLibrary.language ?? 'pt-BR',
              start_time: copyDialogLibrary.start_time,
              end_time: copyDialogLibrary.end_time,
              source_library_id: copyDialogLibrary.id,
            } : null}
          />

          <CampaignAiGuidelinesModal
            open={aiGuidelinesOpen}
            joined={isJoined}
            onDismiss={acknowledgeAiGuidelines}
            onAcknowledge={handleAiGuidelinesAcknowledge}
          />

          <CpmTermsModal
            open={termsOpen}
            onOpenChange={(next) => {
              // If the modal is dismissed without accepting, drop the queued join.
              if (!next) pendingJoinRef.current = false;
              setTermsOpen(next);
            }}
            version={cpmTermsCurrentVersion}
            onAccepted={async () => {
              await refetchMyData();
              if (pendingJoinRef.current) {
                pendingJoinRef.current = false;
                await performJoin();
              }
            }}
          />
        </div>
      </main>
    </Layout>
  );
}
